import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  FormGroup,
  Label,
  Input,
  CardBody,
  Container,
  Button,
  UncontrolledAlert,
  Spinner, // Import Spinner from Reactstrap
} from "reactstrap";
import Dropzone from "react-dropzone";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const MalwareAnalysis = () => {
  // Set page title
  document.title = "Malware Analysis";

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [malwareType, setMalwareType] = useState("");
  const [analysisNotes, setAnalysisNotes] = useState("");
  const [name, setName] = useState(""); // State for Name field

  // State for alert visibility and message
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState("success");
  const [loading, setLoading] = useState(false); // Loading spinner state
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;

  // Handle uploaded files
  const handleAcceptedFiles = (files) => {
    files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setSelectedFiles([...selectedFiles, ...files]);
  };

  // Remove file from the list
  const removeFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  // Format file sizes
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

   

    // Show loading spinner
    setLoading(true);

    // Create a FormData object to hold the form fields and files
    const formData = new FormData();
    formData.append("name", name);
    formData.append("analysisNotes", analysisNotes);

    // Append each selected file to the FormData object
    selectedFiles.forEach((file) => {
      formData.append("file", file); // 'file' is the key sent to the backend
    });

    try {
      // Send the POST request to the backend
      const response = await fetch(`https://api.compliance.cx/malware-scan/${userId}`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setAlertMessage("Malware analysis data has been successfully submitted!");
        setAlertColor("success");
        setAlertVisible(true);

        // Reset form fields
        setName("");
        setAnalysisNotes("");
        setSelectedFiles([]);

        // Scroll to the top of the page with smooth scrolling
        window.scrollTo({
          top: 0,
          behavior: "smooth", // Smooth scroll behavior
        });

        // Automatically hide the alert after 5 seconds
        setTimeout(() => {
          setAlertVisible(false);
        }, 10000);
      } else {
        throw new Error("Failed to submit data.");
      }
    } catch (error) {
      setAlertMessage("Error 121. Please Try Later.");
      setAlertColor("danger");
      setAlertVisible(true);

      // Automatically hide the alert after 5 seconds
      setTimeout(() => {
        setAlertVisible(false);
      }, 5000);
    } finally {
      // Hide the loading spinner after the operation is done
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Scan" breadcrumbItem="Malware Analysis" />

          {/* Success Alert */}
          {alertVisible && (
            <UncontrolledAlert
              color={alertColor}
              className="alert-dismissible fade show"
              role="alert"
            >
              {alertMessage}
            </UncontrolledAlert>
          )}

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    {/* Name Field */}
                    <FormGroup>
                      <Label for="name">Name</Label>
                      <Input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter asset name"
                        required
                      />
                    </FormGroup>

                    {/* Analysis Notes */}
                    <FormGroup>
                      <Label for="analysisNotes">Notes</Label>
                      <Input
                        type="textarea"
                        id="analysisNotes"
                        rows="4"
                        style={{ height: "120px", resize: "none" }} // Fixed height and disable resizing
                        value={analysisNotes}
                        onChange={(e) => setAnalysisNotes(e.target.value)}
                        placeholder="Add any relevant details or observations..."
                      />
                    </FormGroup>

                    {/* File Upload */}
                    <Dropzone onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}>
                      {({ getRootProps, getInputProps }) => (
                        <div className="dropzone">
                          <div
                            className="dz-message needsclick mt-2"
                            {...getRootProps()}
                          >
                            <input {...getInputProps()} />
                            <div className="mb-3">
                              <i className="display-4 text-muted bx bxs-cloud-upload" />
                            </div>
                            <h4>Drop files here or click to upload malware samples.</h4>
                          </div>
                        </div>
                      )}
                    </Dropzone>

                    <div className="dropzone-previews mt-3">
                      {selectedFiles.map((file, i) => (
                        <Card
                          className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                          key={`${file.name}-${i}`}
                        >
                          <div className="p-2">
                            <Row className="align-items-center">
                              <Col className="col-auto">
                                <img
                                  height="80"
                                  className="avatar-sm rounded bg-light"
                                  alt={file.name}
                                  src={file.preview}
                                />
                              </Col>
                              <Col>
                                <p className="text-muted font-weight-bold mb-1">
                                  {file.name}
                                </p>
                                <p className="mb-0">
                                  <strong>{file.formattedSize}</strong>
                                </p>
                              </Col>
                              <Col className="col-auto">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeFile(i)}
                                >
                                  ✕
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </Card>
                      ))}
                    </div>

                    {/* Submit Button */}
                    <div className="text-center mt-4">
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Submit for Analysis"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MalwareAnalysis;
